import type { CreateTimetableRequest, TimetableStructure } from '@schooly/api';
import type { ValidRow } from '@schooly/components/xls-service';

import { GroupWithCode, normalizeRoomPath } from './buildTimetableSchema';

type SlotAllocationInput = NonNullable<CreateTimetableRequest['slot_allocations']>[number];
type SlotAllocationEntry = SlotAllocationInput['allocations'][number];

export type ToSlotAllocationsParams = {
  rows: ValidRow[];
  groups: GroupWithCode[];
  roomPathMap: Map<string, string>;
  structure: TimetableStructure;
};

export function toSlotAllocations({
  rows,
  groups,
  roomPathMap,
  structure,
}: ToSlotAllocationsParams): SlotAllocationInput[] {
  const groupMap = new Map(groups.map((g) => [g.timetable_code, g.id]));
  const slotMap = new Map<string, SlotAllocationEntry[]>();

  for (const { data } of rows) {
    const weekday = structure.weekdays[Number(data.day) - 1];
    const periodId = structure.periods[Number(data.period) - 1]?.id;
    if (!weekday || !periodId) continue;

    const timeSlot = structure.time_slots.find(
      (ts) => ts.weekday === weekday && ts.period_id === periodId,
    );
    if (!timeSlot) continue;

    const groupId = groupMap.get(String(data.group));
    if (!groupId) continue;

    const roomStr = normalizeRoomPath(String(data.room ?? ''));
    const roomId = roomStr ? (roomPathMap.get(roomStr) ?? null) : null;

    const entries = slotMap.get(timeSlot.id) ?? [];
    entries.push({ group_id: groupId, room_id: roomId });
    slotMap.set(timeSlot.id, entries);
  }

  return Array.from(slotMap.entries()).map(([time_slot_id, allocations]) => ({
    time_slot_id,
    allocations,
  }));
}
